/* global globalThis */
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Sector,
} from "recharts";
import {
  AlertTriangle,
  Database,
  Loader,
  AlertCircle,
  Shield,
  Filter,
  X,
} from "lucide-react";
import axiosClientPayload from "../../../../api-client/AxiosClientPayload";
import PIIDashboard from "../upload/utils/PIIDashboard.js";

// ── Extracted sub-dashboard ───────────────────────────────────────────────────
import IssuePredictorDashboard from "../dashboard/IssuePredictorDashboard.jsx";

const api_base_url_dq = process.env.REACT_APP_DQ_BASE_URL;
const api_base_url_issue = process.env.REACT_APP_ISSUE_BASE_URL;
const api_base_url_pii = process.env.REACT_APP_PII_BASE_URL;

// ─── Shared Color Palette ────────────────────────────────────────────────────
const DIMENSION_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#f97316",
];
const getDimColor = (index) =>
  DIMENSION_COLORS[index % DIMENSION_COLORS.length];

// ─── Custom Active Pie Shape ─────────────────────────────────────────────────
const renderActiveShape = (props) => {
  const {
    cx, cy, innerRadius, outerRadius,
    startAngle, endAngle, fill, payload, percent, value,
  } = props;
  return (
    <g>
      <text x={cx} y={cy - 12} textAnchor="middle" fill={fill} style={{ fontWeight: 700, fontSize: 13 }}>
        {payload.name}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#374151" style={{ fontSize: 12 }}>
        {`${(percent * 100).toFixed(1)}%`}
      </text>
      <text x={cx} y={cy + 28} textAnchor="middle" fill="#6b7280" style={{ fontSize: 11 }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 12} outerRadius={outerRadius + 16}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
    </g>
  );
};

// ─── Custom Bar Tooltip ──────────────────────────────────────────────────────
const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-4 min-w-[180px]">
      <p className="text-sm font-bold text-gray-800 mb-2 border-b border-gray-100 pb-2">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center justify-between gap-4 mt-1">
          <span className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: entry.fill }} />
            {entry.name}
          </span>
          <span className="text-xs font-semibold text-gray-800">{entry.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

CustomBarTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
};

// ─── Dimension Filter Badge ──────────────────────────────────────────────────
const DimensionBadge = ({ name, color, onClear }) => (
  <div
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-medium shadow-md"
    style={{ background: `linear-gradient(135deg, ${color}dd, ${color})` }}
  >
    <Filter className="w-3.5 h-3.5" />
    Filtered: {name}
    <button onClick={onClear} className="ml-1 hover:bg-white hover:bg-opacity-20 rounded-full p-0.5 transition-all">
      <X className="w-3.5 h-3.5" />
    </button>
  </div>
);

DimensionBadge.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  onClear: PropTypes.func.isRequired,
};

// ─── Helper for legend button styling ────────────────────────────────────────
const getLegendButtonClass = (isActive, activeDimension) => {
  if (isActive) return "shadow-md scale-105 border-current";
  if (activeDimension) return "opacity-35 border-gray-200 hover:opacity-60";
  return "border-transparent hover:border-gray-200 hover:bg-gray-50";
};

// ─── Clickable Legend ────────────────────────────────────────────────────────
const ClickableLegend = ({ labels, activeDimension, onLegendClick }) => (
  <div className="flex flex-wrap justify-center gap-2 mt-2">
    {labels.map((label, index) => {
      const isActive = activeDimension?.name === label;
      const color = getDimColor(index);
      return (
        <button
          key={label}
          onClick={() => onLegendClick(label, index)}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${getLegendButtonClass(isActive, activeDimension)}`}
          style={{ color }}
        >
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
          {label}
        </button>
      );
    })}
  </div>
);

ClickableLegend.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDimension: PropTypes.shape({ name: PropTypes.string.isRequired, color: PropTypes.string.isRequired }),
  onLegendClick: PropTypes.func.isRequired,
};

// ─── Helper for score color ──────────────────────────────────────────────────
const getScoreColorClass = (score) => {
  if (score >= 90) return "text-green-600";
  if (score >= 70) return "text-yellow-600";
  return "text-red-600";
};

// ─── Filter Status Panel ─────────────────────────────────────────────────────
const FilterPanel = ({ activeDimension, filteredStats, onClear }) => {
  if (!activeDimension || !filteredStats) return null;
  return (
    <div
      className="rounded-xl p-5 flex items-center justify-between shadow-sm flex-wrap gap-3"
      style={{
        background: `${activeDimension.color}0c`,
        border: `1px solid ${activeDimension.color}30`,
        borderLeft: `4px solid ${activeDimension.color}`,
      }}
    >
      <div className="flex items-center gap-4 flex-wrap">
        <DimensionBadge name={activeDimension.name} color={activeDimension.color} onClear={onClear} />
        <div className="flex items-center gap-5 text-sm flex-wrap">
          {filteredStats.total !== undefined && (
            <span className="text-gray-500">Records: <strong className="text-gray-800">{filteredStats.total?.toLocaleString()}</strong></span>
          )}
          {filteredStats.success !== undefined && (
            <span className="text-green-600">Passed: <strong>{filteredStats.success?.toLocaleString()}</strong></span>
          )}
          {filteredStats.failed !== undefined && (
            <span className="text-red-500">Failed: <strong>{filteredStats.failed?.toLocaleString()}</strong></span>
          )}
          {filteredStats.score !== undefined && (
            <span className={`font-semibold ${getScoreColorClass(filteredStats.score)}`}>
              Score: {filteredStats.score?.toFixed(1)}%
            </span>
          )}
        </div>
      </div>
      <button
        onClick={onClear}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-700 transition-all"
      >
        <X className="w-3.5 h-3.5" /> Show All
      </button>
    </div>
  );
};

FilterPanel.propTypes = {
  activeDimension: PropTypes.shape({ name: PropTypes.string.isRequired, color: PropTypes.string.isRequired }).isRequired,
  filteredStats: PropTypes.shape({
    total: PropTypes.number, success: PropTypes.number, failed: PropTypes.number, score: PropTypes.number,
  }).isRequired,
  onClear: PropTypes.func.isRequired,
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN: DataQualityDashboard
// ════════════════════════════════════════════════════════════════════════════
const DataQualityDashboard = () => {
  const [loggedInUser, setLoggedInUser] = useState("");
  const [permissions, setPermissions] = useState(JSON.parse(sessionStorage.getItem("rolesAndPermission") || "{}"));
  const [activeTab, setActiveTab] = useState("");
  const [error, setError] = useState(null);
  const [dqLoading, setDqLoading] = useState(false);
  const [issueLoading, setIssueLoading] = useState(false);
  const [piiLoading, setPiiLoading] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(
    localStorage.getItem("organization_config_id") || ""
  );

  // Tab data
  const [dqDashboardData, setDqDashboardData] = useState(null);
  const [issuePredictorData, setIssuePredictorData] = useState(null);
  const [threadPayload, setThreadPayload] = useState(null);

  // ── DQ Dashboard interactive state ────────────────────────────────────────
  const [dqActiveDimension, setDqActiveDimension] = useState(null);
  const [dqActivePieIndex, setDqActivePieIndex] = useState(null);

  const [permissionsReady, setPermissionsReady] = useState(false);

  const resetAllDashboardData = useCallback(() => {
    setDqDashboardData(null);
    setIssuePredictorData(null);
    setDqActiveDimension(null);
    setDqActivePieIndex(null);
  }, []);

  useEffect(() => {
    const handlePermissionsReady = (e) => {
      // Only allow fetch if permissions are actually loaded successfully
      if (e.detail) {
        setPermissions(e.detail);
        const currentConfig = localStorage.getItem("organization_config_id") || "";
        setSelectedConfig(currentConfig);
        setPermissionsReady(true);
      } else {
        setPermissionsReady(false);
      }
    };

    const handleStorage = (e) => {
      if (e.detail?.key === "organization_config_id") {
        setPermissionsReady(false);
        resetAllDashboardData();
      }
    };

    globalThis.addEventListener("permissionsUpdated", handlePermissionsReady);
    globalThis.addEventListener("customStorageChange", handleStorage);

    // Initial check on mount
    const currentConfig = localStorage.getItem("organization_config_id");
    const roles = sessionStorage.getItem("rolesAndPermission");
    if (currentConfig && roles) {
      const parsedRoles = JSON.parse(roles);
      setPermissions(parsedRoles);
      setSelectedConfig(currentConfig);
      setPermissionsReady(true);
    }

    return () => {
      globalThis.removeEventListener("permissionsUpdated", handlePermissionsReady);
      globalThis.removeEventListener("customStorageChange", handleStorage);
    };
  }, [resetAllDashboardData]);

  // ── Fetch logged-in user ───────────────────────────────────────────────────
  useEffect(() => {
    try {
      const accountKeys = JSON.parse(sessionStorage.getItem("msal.account.keys"));
      if (accountKeys?.[0]) {
        const user = JSON.parse(sessionStorage.getItem(accountKeys[0]));
        setLoggedInUser(user.name.split(" "));
      }
    } catch {
      setLoggedInUser(["User"]);
    }
  }, []);

  // ── API fetchers ───────────────────────────────────────────────────────────
  const fetchDQDashboard = async () => {
    try {
      setDqLoading(true);
      setError(null);
      const token = localStorage.getItem("token") || "";
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";
      const response = await axiosClientPayload.post(
        `${api_base_url_dq}/dashboard`,
        { token, organization_config_id: organizationConfigId },
      );
      setDqDashboardData(response.data);
    } catch (err) {
      console.error("Error fetching DQ dashboard data:", err);
    } finally {
      setDqLoading(false);
    }
  };


  const fetchIssuePredictorDashboard = async () => {
    try {
      setIssueLoading(true);
      setError(null);
      const token = localStorage.getItem("token") || "";
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";

      // ── Resolve userId from MSAL session ─────────────────────────────────
      let userId = "";
      try {
        const accountKey = JSON.parse(sessionStorage.getItem("msal.account.keys"))?.[0];
        if (accountKey) {
          const accountData = JSON.parse(sessionStorage.getItem(accountKey));
          const email = accountData?.username || "";
          if (email) {
            const axiosClient = (await import("../../../../api-client/AxiosClient")).default;
            const usersResp = await axiosClient.get("/api/User/all");
            const allUsers = Array.isArray(usersResp?.data) ? usersResp.data : [];
            const norm = email.toLowerCase().trim();
            const match = allUsers.find((u) => u.email?.toLowerCase().trim() === norm)
              || allUsers.find((u) => u.email && (u.email.toLowerCase().includes(norm) || norm.includes(u.email.toLowerCase())));
            userId = match?.userId || match?.id || "";
          }
        }
      } catch (_) { /* userId stays "" — RecentAnalysis will still render */ }

      // ── Store full payload and call the list endpoint ─────────────────────
      const payload = { token, organizationConfigId, userId };
      setThreadPayload(payload);

      const response = await axiosClientPayload.post(
        `${api_base_url_issue}/thread-history/list`,
        payload,
      );
      setIssuePredictorData(response.data);   // ← stores thread list for RecentAnalysis seed
    } catch (err) {
      console.error("Error fetching issue predictor data:", err);
    } finally {
      setIssueLoading(false);
    }
  };
  const fetchPIIDashboard = async () => {
    try {
      setPiiLoading(true);
      setError(null);
      const token = localStorage.getItem("token") || "";
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";
      await axiosClientPayload.post(
        `${api_base_url_pii}/dashboard`,
        { token, org_config_id: organizationConfigId },
      );
    } catch (err) {
      console.error("Error fetching PII dashboard data:", err);
    } finally {
      setPiiLoading(false);
    }
  };

  useEffect(() => {
    if (permissionsReady && selectedConfig) {
      switch (activeTab) {
        case "dq-dashboard":
          fetchDQDashboard();
          break;
        case "issue-predictor":
          fetchIssuePredictorDashboard();
          break;
        case "pii-dashboard":
          fetchPIIDashboard();
          break;
        default:
          break;
      }
    }
  }, [activeTab, selectedConfig, permissionsReady]);

  // ── Tab config with permission filtering ──────────────────────────────
  const { DQ, IssuePredicit, PII } = permissions;
  const tabs = [
    { id: "dq-dashboard", label: "DQ Dashboard", icon: Database, color: "blue", visible: DQ?.canView },
    { id: "issue-predictor", label: "Issue Predict Dashboard", icon: AlertTriangle, color: "orange", visible: IssuePredicit?.canView },
    { id: "pii-dashboard", label: "PII Dashboard", icon: Shield, color: "green", visible: PII?.canView },
  ].filter(tab => tab.visible);

  useEffect(() => {
    if (tabs.length > 0 && !activeTab) {
      setActiveTab(tabs[0].id);
    } else if (tabs.length > 0 && activeTab && !tabs.some(t => t.id === activeTab)) {
      // If active tab was set but is no longer visible (e.g. permission change), reset to first available
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  // ── Utility helpers ────────────────────────────────────────────────────────
  const getQualityColor = (score) => {
    const n = typeof score === "string" ? Number.parseFloat(score) : score;
    if (n >= 90) return "#22c55e";
    if (n >= 70) return "#1D4ED8";
    return "#FC8716";
  };
  const parsePercentage = (v) =>
    typeof v === "string" ? Number.parseFloat(v.replace("%", "")) : v;
  const getOverallStatus = (qs) => {
    const s = typeof qs === "string" ? parsePercentage(qs) : qs;
    if (s < 50) return "Low";
    if (s < 75) return "Medium";
    return "High";
  };
  const getStatusColor = (status) => {
    switch (status) {
      case "High": return "#22c55e";
      case "Medium": return "#1D4ED8";
      case "Low": return "#FC8716";
      default: return "#6B7280";
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  // DQ DASHBOARD — Interactive handlers
  // ════════════════════════════════════════════════════════════════════════════
  const dqApiData = dqDashboardData ? dqDashboardData.data || dqDashboardData : null;
  const dqDimensionSummary = dqApiData?.dimension_summary || [];

  const handleDqPieClick = useCallback(
    (data, index) => {
      const dimName = data?.name;
      if (!dimName) return;
      if (dqActiveDimension?.name === dimName) {
        setDqActiveDimension(null);
        setDqActivePieIndex(null);
      } else {
        setDqActiveDimension({ name: dimName, color: getDimColor(index) });
        setDqActivePieIndex(index);
      }
    },
    [dqActiveDimension],
  );

  const handleDqBarClick = useCallback(
    (data) => {
      const dimName = data?.activeLabel;
      if (!dimName) return;
      const idx = dqDimensionSummary.findIndex((d) => d.dimension === dimName);
      if (dqActiveDimension?.name === dimName) {
        setDqActiveDimension(null);
        setDqActivePieIndex(null);
      } else {
        setDqActiveDimension({ name: dimName, color: getDimColor(Math.max(idx, 0)) });
        setDqActivePieIndex(null);
      }
    },
    [dqActiveDimension, dqDimensionSummary],
  );

  const clearDqFilter = () => {
    setDqActiveDimension(null);
    setDqActivePieIndex(null);
  };

  const dqFilteredStats = dqActiveDimension
    ? (() => {
      const dim = dqDimensionSummary.find((d) => d.dimension === dqActiveDimension.name);
      if (!dim) return null;
      const total = dim.success_records + dim.failed_records;
      return { total, success: dim.success_records, failed: dim.failed_records, score: dim.percentage };
    })()
    : null;

  const dqKpiOverride =
    dqActiveDimension && dqFilteredStats
      ? {
        totalRecords: dqFilteredStats.total,
        successRecords: dqFilteredStats.success,
        failedRecords: dqFilteredStats.failed,
        score: dqFilteredStats.score,
      }
      : null;

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER: DQ DASHBOARD
  // ════════════════════════════════════════════════════════════════════════════
  const renderDQDashboard = () => {
    if (dqLoading) {
      return (
        <div className="flex justify-center py-16">
          <Loader className="h-8 w-8 text-blue-600 animate-spin" />
        </div>
      );
    }
    if (!dqDashboardData) return null;

    const apiData = dqDashboardData.data || dqDashboardData;
    const qualityScore = apiData.overall_score || 0;

    const qualityDistribution = (apiData.dimension_summary || []).map((dim) => ({
      Dimension: dim.dimension,
      Avg_Success_Rate: dim.percentage,
      Success: dim.success_records,
      Fail: dim.failed_records,
    }));

    const pieLabels = apiData.dimension_pie_chart?.labels || [];
    const pieValues = apiData.dimension_pie_chart?.values || [];
    const barLabels = qualityDistribution.map((d) => d.Dimension);

    return (
      <div className="space-y-6">
        {/* ── KPI Cards ───────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-8 transition-all duration-300 ease-in-out hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg hover:scale-[1.03] cursor-pointer">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">Overall Database Quality</h2>
            <p className="text-4xl font-medium" style={{ color: getQualityColor(qualityScore) }}>
              {qualityScore.toFixed(2)}%
            </p>
          </div>

          <div
            className="bg-white rounded-xl border border-gray-200 p-8 transition-all duration-300 ease-in-out hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg hover:scale-[1.03] cursor-pointer"
            style={dqActiveDimension ? { borderColor: dqActiveDimension.color, background: `${dqActiveDimension.color}08` } : {}}
          >
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
              {dqActiveDimension ? "Passed Records" : "Total Tables"}
            </h2>
            <p className="text-4xl font-medium text-blue-600">
              {dqActiveDimension
                ? (dqFilteredStats?.success?.toLocaleString() ?? "—")
                : apiData.total_tables_analyzed || 0}
            </p>
          </div>

          <div
            className="bg-white rounded-xl border border-gray-200 p-8 transition-all duration-300 ease-in-out hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg hover:scale-[1.03] cursor-pointer"
            style={dqActiveDimension ? { borderColor: dqActiveDimension.color, background: `${dqActiveDimension.color}08` } : {}}
          >
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
              {dqActiveDimension ? "Dimension Score" : "Overall Status"}
            </h2>
            <p className="text-4xl font-medium text-blue-600">
              {(dqKpiOverride?.totalRecords ?? apiData.total_records_analyzed)?.toLocaleString() || 0}
            </p>
          </div>

          <div
            className="bg-white rounded-xl border border-gray-200 p-8 transition-all duration-300 ease-in-out hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg hover:scale-[1.03] cursor-pointer"
            style={dqActiveDimension ? { borderColor: dqActiveDimension.color, background: `${dqActiveDimension.color}08` } : {}}
          >
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
              {dqActiveDimension ? "Dimension Score" : "Overall Status"}
            </h2>
            <p
              className="text-4xl font-medium"
              style={{
                color: dqActiveDimension
                  ? getQualityColor(dqKpiOverride?.score ?? qualityScore)
                  : getStatusColor(getOverallStatus(qualityScore)),
              }}
            >
              {dqActiveDimension
                ? `${(dqKpiOverride?.score ?? qualityScore).toFixed(1)}%`
                : apiData.overall_status || getOverallStatus(qualityScore)}
            </p>
          </div>
        </div>

        {/* ── Active Filter Banner ─────────────────────────────────────────────── */}
        {dqActiveDimension && (
          <FilterPanel activeDimension={dqActiveDimension} filteredStats={dqFilteredStats} onClear={clearDqFilter} />
        )}

        {/* ── Charts ──────────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar Chart */}
          {qualityDistribution.length > 0 && (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 lg:p-8">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Overall Quality Distribution</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">Click a bar to filter the dashboard by that dimension</p>
                </div>
                {dqActiveDimension && (
                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-medium whitespace-nowrap">
                    Click again to reset
                  </span>
                )}
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={qualityDistribution} onClick={handleDqBarClick} style={{ cursor: "pointer" }} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="Dimension" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload?.[0]) {
                        const d = payload[0].payload;
                        return (
                          <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                            <p className="font-semibold text-sm text-gray-900">{d.Dimension}</p>
                            <p className="text-xs text-green-600 mt-1">Success: {d.Avg_Success_Rate}%</p>
                            <p className="text-xs text-gray-600">Passed: {d.Success?.toLocaleString()}</p>
                            <p className="text-xs text-gray-600">Failed: {d.Fail?.toLocaleString()}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                    contentStyle={{ backgroundColor: "transparent", border: "none" }}
                  />
                  <Bar dataKey="Avg_Success_Rate" name="Success Rate (%)">
                    {qualityDistribution.map((entry, index) => {
                      const isHighlighted = !dqActiveDimension || dqActiveDimension.name === entry.Dimension;
                      return (
                        <Cell key={entry.Dimension} fill={getDimColor(index)} opacity={isHighlighted ? 1 : 0.2} style={{ transition: "opacity 0.25s ease" }} />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <ClickableLegend
                labels={barLabels}
                activeDimension={dqActiveDimension}
                onLegendClick={(label) => handleDqBarClick({ activeLabel: label })}
              />
            </div>
          )}

          {/* Pie Chart */}
          {pieLabels.length > 0 && (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 lg:p-8">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Dimension Distribution</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">Click a slice or legend to filter the dashboard</p>
                </div>
                {dqActiveDimension && (
                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-medium whitespace-nowrap">
                    Click again to reset
                  </span>
                )}
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieLabels.map((label, i) => ({ name: label, value: pieValues[i] }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    activeIndex={dqActivePieIndex}
                    activeShape={renderActiveShape}
                    onClick={(data, index) => handleDqPieClick(data, index)}
                    style={{ cursor: "pointer" }}
                  >
                    {pieLabels.map((label, index) => (
                      <Cell
                        key={label}
                        fill={getDimColor(index)}
                        opacity={!dqActiveDimension || dqActiveDimension.name === label ? 1 : 0.2}
                        style={{ transition: "opacity 0.25s ease" }}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => value.toLocaleString()} />
                  <Legend
                    content={() => (
                      <ClickableLegend
                        labels={pieLabels}
                        activeDimension={dqActiveDimension}
                        onLegendClick={(label, index) => handleDqPieClick({ name: label }, index)}
                      />
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER: PII DASHBOARD
  // ════════════════════════════════════════════════════════════════════════════
  const renderPIIDashboard = () => {
    if (piiLoading) {
      return (
        <div className="flex justify-center py-16">
          <Loader className="h-8 w-8 text-blue-600 animate-spin" />
        </div>
      );
    }
    return <PIIDashboard />;
  };

  // ════════════════════════════════════════════════════════════════════════════
  // ROOT RENDER
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-800 ml-5 mt-2 via-blue-500 to-orange-500 rounded-3xl p-10 text-white max-w-full mb-4">
        <h1 className="text-4xl font-bold mb-4 w-full">
          Welcome to Owl Sight Workspace, <br />
          {loggedInUser?.[0]}
          {loggedInUser?.length > 1 && " " + loggedInUser.at(-1)}!
        </h1>
        <p className="text-lg">
          BlueGecko Owl Sight is a comprehensive migration data quality solution.
          <br />
          With Owl Sight, enterprises gain confidence that every dataset is accurate, compliant, and migration-ready
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 px-6 mb-6">
        <nav className="flex space-x-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-semibold text-sm transition-all border-b-2 whitespace-nowrap ${isActive
                  ? `text-${tab.color}-600 border-${tab.color}-600 bg-${tab.color}-50`
                  : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50"
                  }`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 px-6 pb-6">
        <main className="flex-1">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {activeTab === "dq-dashboard" && renderDQDashboard()}

          {/* ── Issue Predictor — delegated to its own component ─────────────── */}
          {activeTab === "issue-predictor" && (
            <IssuePredictorDashboard
              issuePredictorData={issuePredictorData}
              issueLoading={issueLoading}
              threadPayload={threadPayload}
            />
          )}

          {activeTab === "pii-dashboard" && renderPIIDashboard()}
        </main>
      </div>
    </div>
  );
};

export default DataQualityDashboard;